/**
 * The rotation arithmetic behind the photo viewer, kept apart from the component
 * so it can be tested — anything that imports `react-native` cannot be, because
 * the package ships Flow syntax the test runner will not parse.
 *
 * Rotation is free for the focus blur (the overlay is a sibling of the photo
 * inside the same transformed view, so any transform carries both). It is NOT
 * free for the framing: the fit scale, the pan clamp and the focus centring all
 * have to work off the photo's rotated footprint rather than its own width and
 * height, and that is what lives here.
 */

/** A quarter turn, in radians — the angle labels on these bottles actually want. */
export const QUARTER_TURN = Math.PI / 2;

/**
 * Release within this of a quarter turn and it snaps there. Rotation is
 * genuinely free — a label photographed at a slight angle can be left at that
 * angle — but a photo abandoned 3° off square reads as a mistake rather than a
 * choice, and the upright positions are where nearly every twist is heading.
 */
export const ROTATION_SNAP_WINDOW = (12 * Math.PI) / 180;

/**
 * The axis-aligned box a `width` x `height` rectangle occupies once rotated.
 *
 * Everything that used to read the photo's width and height directly reads this
 * instead: rotated 90°, a portrait photo is wider than a portrait viewport and
 * shorter than it, and both the fit scale and the pan clamp have to know that.
 * Absolute cosines, so turning left and turning right clamp identically.
 */
export function rotatedExtent(
    width: number,
    height: number,
    rotation: number
): { width: number; height: number } {
    const cos = Math.abs(Math.cos(rotation));
    const sin = Math.abs(Math.sin(rotation));
    return { width: width * cos + height * sin, height: width * sin + height * cos };
}

/** Wraps an angle difference into (-π, π], so a twist past half a turn does not jump. */
export function unwrap(delta: number): number {
    return Math.atan2(Math.sin(delta), Math.cos(delta));
}

/**
 * Turns an offset by `rotation`.
 *
 * The viewer's transforms compose as translate · rotate · scale, so a point
 * lands at R(S(p)) + t. Centring something therefore means undoing its offset
 * AFTER rotation — translating by the raw offset would send the photo off at a
 * tangent on anything but a square-on view.
 */
export function rotatePoint(x: number, y: number, rotation: number): { x: number; y: number } {
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    return { x: x * cos - y * sin, y: x * sin + y * cos };
}

/**
 * The scale at which a `contentWidth` x `contentHeight` photo, rotated by this
 * much, exactly fits a `width` x `height` viewport.
 *
 * Capped at 1: the photo arrives already `contain`ed, so square-on there is
 * nothing to gain by growing it. Rotated it drops below 1, which is why the
 * viewer's zoom floor cannot be the constant it used to be.
 */
export function fittedScale(
    contentWidth: number,
    contentHeight: number,
    viewportWidth: number,
    viewportHeight: number,
    rotation: number
): number {
    const extent = rotatedExtent(contentWidth, contentHeight, rotation);
    return Math.min(1, viewportWidth / extent.width, viewportHeight / extent.height);
}

/** The nearest quarter turn, if the angle is close enough to be worth magnetising. */
export function snapRotation(rotation: number): number {
    const snapped = Math.round(rotation / QUARTER_TURN) * QUARTER_TURN;
    return Math.abs(snapped - rotation) > ROTATION_SNAP_WINDOW ? rotation : snapped;
}
